import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  IconButton,
  Typography,
  Avatar,
  CircularProgress,
  Backdrop,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import {
  Close as CloseIcon,
  Remove as MinimizeIcon,
  OpenInFull as MaximizeIcon,
  CloseFullscreen as RestoreIcon,
  MoreVert as MoreVertIcon,
  Info as InfoIcon,
  PersonAdd as PersonAddIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  VolumeOff as VolumeOffIcon,
  Report as ReportIcon,
  Settings as SettingsIcon,
  LockOpen as LockOpenIcon,
  DeleteSweep as DeleteSweepIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { MEDIA_URL } from "src/network/endpoints";
import { ApiEndPoints } from "src/network/endpoints";
import { axiosInstance } from "src/network/adapter";

// Import the existing ChatView component - reuse, don't duplicate
import ChatView from "src/pages/chat/components/ChatView";
import ChatHeader from "src/pages/chat/components/ChatHeader";
import { useChatPopup } from "../../context/ChatPopupContext";
import { useSocketContext } from "../../context/SocketContext";
import { useSelector } from "react-redux";
import authConfig from "src/configs/auth";

// Styled components
const PopupContainer = styled(Box)(({ minimized, maximized }) => ({
  position: "fixed",
  bottom: 0,
  // Normal: 380x520, Maximized: 900x750 (centered on screen), Minimized: 380x52
  width: maximized ? 900 : 380,
  height: minimized ? 52 : maximized ? 750 : 520,
  // When maximized, center on screen instead of right alignment
  left: maximized ? "50%" : "auto",
  transform: maximized ? "translateX(-50%)" : "none",
  backgroundColor: "#fff",
  borderRadius: maximized ? "12px" : "12px 12px 0 0",
  boxShadow: maximized
    ? "0 8px 32px rgba(0, 0, 0, 0.25)"
    : "0 -4px 24px rgba(0, 0, 0, 0.15)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  transition: "all 0.3s ease-in-out",
  zIndex: 1300,
  // Add bottom margin when maximized for better visibility
  bottom: maximized ? 20 : 0,
}));

const PopupHeader = styled(Box)(({ theme, clickable }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1, 1.5),
  backgroundColor: "#6200EE",
  color: "#fff",
  cursor: clickable ? "pointer" : "default",
  minHeight: 52,
  gap: theme.spacing(1),
  flexShrink: 0,
}));

const PopupControls = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 4,
  marginLeft: "auto",
});

const ChatContainer = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  position: "relative",
  "& > div": {
    height: "100% !important",
    maxHeight: "100% !important",
  },
});

const OnlineStatus = styled(Box)(({ theme, isOnline }) => ({
  position: "absolute",
  bottom: 0,
  right: 0,
  width: 10,
  height: 10,
  borderRadius: "50%",
  backgroundColor: isOnline
    ? theme.palette.success.main
    : theme.palette.grey[400],
  border: `2px solid #6200EE`, // Match popup header background
  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
}));

/**
 * ChatPopup Component
 *
 * A floating chat panel that REUSES the existing ChatView component.
 * This ensures all chat functionality (messages, sockets, read receipts, etc.)
 * works exactly the same as the main chat screen.
 *
 * Props:
 * @param {string} popupId - Unique popup ID
 * @param {string} chatId - Entity ID (user/business/event)
 * @param {string} entityType - Type of entity (place, event, user, business)
 * @param {Object} entityData - Entity information (name, image, etc.)
 * @param {boolean} minimized - Whether popup is minimized
 * @param {Object} position - Position { right, bottom }
 * @param {Function} onClose - Close handler
 * @param {Function} onToggleMinimize - Minimize toggle handler
 */
const ChatPopup = ({
  popupId,
  chatId,
  entityType,
  entityData,
  minimized,
  maximized,
  position,
  onClose,
  onToggleMinimize,
  onToggleMaximize,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [conversationInfo, setConversationInfo] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [oppositeUserOnline, setOppositeUserOnline] = useState(false);
  const [canViewOrder, setCanViewOrder] = useState(false);
  const [canCloseOrder, setCanCloseOrder] = useState(false);
  
  // Create ref to access ChatView's exposed methods
  const chatViewRef = useRef(null);
  
  const { 
    cacheConversationId, 
    getCachedConversationId,
    getCachedMessages,
    cacheMessages 
  } = useChatPopup();
  const { emit, addEventListener, isConnected, getConversations } = useSocketContext();
  
  const currentUser = useSelector((state) => state.auth.user);
  const storedUserId =
    typeof window !== "undefined"
      ? window.localStorage.getItem(authConfig.storageUserIdKeyName)
      : null;
  const currentUserId = currentUser?.userId ?? storedUserId;
  
  const isMenuOpen = Boolean(menuAnchorEl);
  
  // Menu handlers
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const handleOpenGroupInfo = () => {
    // Call ChatView's exposed handler
    if (chatViewRef.current) {
      chatViewRef.current.handleOpenGroupInfo();
    }
  };
  
  // Menu action handler that forwards to ChatView
  const handleMenuAction = (action) => {
    console.log("Popup menu action:", action);
    handleMenuClose();
    
    // Forward action to ChatView's handler
    if (chatViewRef.current && chatViewRef.current.handleMenuAction) {
      chatViewRef.current.handleMenuAction(action);
    } else {
      console.warn("ChatView ref not available for action:", action);
    }
  };

  // Initialize conversation - get or create conversation ID
  // Conversation ID is cached so reopening the same chat will use cached ID
  useEffect(() => {
    const initializeChat = async () => {
      if (!chatId) return;

      setIsLoading(true);

      try {
        let convId = null;

        // Check if we have a cached conversation ID first
        const cachedConvId = getCachedConversationId(chatId);
        if (cachedConvId) {
          convId = cachedConvId;
        } else {
          // Fetch conversation ID from API
          if (entityType === "user") {
            // Check if one-to-one conversation exists with this user
            try {
              const response = await axiosInstance.post(
                ApiEndPoints.CHAT.checkOneToOneExist,
                JSON.stringify({ userId: chatId })
              );

              if (response.data?.data?._id) {
                convId = response.data.data._id;
                cacheConversationId(chatId, convId);
              } else if (response.data?.data?.conversationId) {
                // Alternative response format
                convId = response.data.data.conversationId;
                cacheConversationId(chatId, convId);
              }
            } catch (apiError) {
              // API failed - this is normal for new conversations or if user doesn't exist in chat DB yet
              // The conversation will be created when the first message is sent
              console.log("No existing conversation found, will create on first message");
              // Don't set convId, will be treated as new conversation
            }
          } else if (entityType === "place" || entityType === "business") {
            // For business/place, use socket to find conversation
            // API may not support business entity types, so fallback to socket
            console.log("Using socket to find business/place conversation");
            if (isConnected) {
              convId = await new Promise((resolve) => {
                const handleConversationList = (data) => {
                  const payload = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.conversations)
                    ? data.conversations
                    : [];

                  // Find conversation with matching businessLocationId
                  const businessConversation = payload.find(
                    (conv) => {
                      const businessId = conv.businessLocationId?._id || conv.businessLocationId;
                      return businessId && businessId.toString() === chatId.toString();
                    }
                  );

                  if (businessConversation) {
                    cacheConversationId(chatId, businessConversation._id);
                    resolve(businessConversation._id);
                  } else {
                    // Business conversation not found - will create new
                    resolve(null);
                  }
                };

                // Listen for conversation list response
                const unsubscribe = addEventListener("conversationList", handleConversationList);

                // Request conversation list
                emit("getConversations", {});

                // Cleanup listener after 5 seconds
                setTimeout(() => {
                  if (unsubscribe) unsubscribe();
                  resolve(null); // Timeout fallback
                }, 5000);
              });
            }
          } else if (entityType === "event") {
            // For events, we need to find the conversation with matching eventId
            // Use socket to get conversations and find the event group chat
            if (isConnected) {
              convId = await new Promise((resolve) => {
                const handleConversationList = (data) => {
                  const payload = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.conversations)
                    ? data.conversations
                    : [];

                  // Find conversation with matching eventId
                  const eventConversation = payload.find(
                    (conv) => conv.eventId && conv.eventId.toString() === chatId.toString()
                  );

                  if (eventConversation) {
                    cacheConversationId(chatId, eventConversation._id);
                    resolve(eventConversation._id);
                  } else {
                    // Event conversation not found - might be a new event without group chat yet
                    resolve(null);
                  }
                };

                // Listen for conversation list response
                const unsubscribe = addEventListener("conversationList", handleConversationList);

                // Request conversation list
                emit("getConversations", {});

                // Cleanup listener after 5 seconds
                setTimeout(() => {
                  if (unsubscribe) unsubscribe();
                  resolve(null); // Timeout fallback
                }, 5000);
              });
            }
          }
        }

        // Build selectedContact object that ChatView expects
        // IMPORTANT: ChatView uses selectedContact.id as the conversation ID
        // and selectedContact.isNewConversation to determine if it's a new chat
        const contact = {
          id: convId || chatId, // This is what ChatView uses as conversationId
          name: entityData?.name || entityData?.fullName || entityData?.userName || "Chat",
          image: entityData?.image || entityData?.profilePic,
          isNewConversation: !convId, // If no convId, it's a new conversation
          isGroup: entityType === "event" ? true : false, // Events are group chats
          userData:
            entityType === "user" ? { _id: chatId, ...entityData } : null,
          businessData:
            entityType === "place" || entityType === "business"
              ? { _id: chatId, ...entityData }
              : null,
          eventData:
            entityType === "event"
              ? { _id: chatId, ...entityData }
              : null,
        };

        console.log("✅ Chat initialized:", {
          conversationId: convId,
          isNewConversation: !convId,
          entityType,
          chatId,
        });

        setSelectedContact(contact);

        // Fetch conversation info if we have a conversation ID
        if (convId && isConnected) {
          // Listen for conversation info response
          const unsubscribe = addEventListener("conversationsInfo", (data) => {
            if (data && data.conversations) {
              setConversationInfo(data.conversations);
              
              // Initialize opposite user online status for one-to-one conversations
              if (!data.conversations.isGroup && data.conversations.users) {
                const oppositeUser = data.conversations.users.find(
                  (user) => user?.userId?._id && user.userId._id !== currentUserId
                );
                if (oppositeUser && oppositeUser.userId) {
                  setOppositeUserOnline(oppositeUser.userId.isOnline || false);
                  // Also update the contact object with oppositeUserId for tracking
                  setSelectedContact(prev => ({
                    ...prev,
                    oppositeUserId: oppositeUser.userId._id,
                    oppositeUserOnline: oppositeUser.userId.isOnline || false
                  }));
                }
              }
            }
          });

          // Request conversation info
          emit("getConversationsInfo", { conversationId: convId });

          // Cleanup listener after 10 seconds
          setTimeout(() => {
            if (unsubscribe) unsubscribe();
          }, 10000);
        }
      } catch (error) {
        console.error("Error initializing chat:", error);
        // Fallback - create contact without conversation ID
        setSelectedContact({
          id: chatId,
          name: entityData?.name || "Chat",
          image: entityData?.image,
          isNewConversation: true,
        });
      }

      setIsLoading(false);
    };

    initializeChat();
  }, [chatId, entityType, entityData, cacheConversationId, getCachedConversationId, emit, addEventListener, isConnected]);

  // Listen for user status updates (online/offline)
  useEffect(() => {
    if (!selectedContact || selectedContact.isGroup) return;

    const unsubscribe = addEventListener("userStatusUpdate", (data) => {
      // Only update if this is the opposite user in this conversation
      if (data && data.userId && selectedContact.oppositeUserId === data.userId) {
        setOppositeUserOnline(data.isOnline || false);
        // Also update the contact object
        setSelectedContact(prev => ({
          ...prev,
          oppositeUserOnline: data.isOnline || false
        }));
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedContact, addEventListener]);

  // Get entity display info
  const getEntityInfo = () => {
    if (!entityData) return { name: "Chat", image: null };

    return {
      name:
        entityData.name ||
        entityData.businessLocationName ||
        entityData.fullName ||
        "Chat",
      image:
        entityData.image ||
        entityData.locationProfilePhoto ||
        entityData.profilePic,
    };
  };

  const { name, image } = getEntityInfo();
  const avatarSrc = image
    ? image.startsWith("http")
      ? image
      : `${MEDIA_URL}${image}`
    : null;

  // Handle close from inside ChatView (memoized so ChatView does not re-render on parent re-renders)
  const handleChatViewClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  return (
    <>
      {/* Backdrop for maximized mode */}
      <Backdrop
        open={maximized && !minimized}
        onClick={onToggleMaximize}
        sx={{
          zIndex: 1299, // Just below popup
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      />
      
      <PopupContainer 
        minimized={minimized} 
        maximized={maximized}
        sx={{ 
          // Only apply right positioning when not maximized
          right: maximized ? "auto" : (position?.right || 20),
        }}
      >
        {/* Header */}
        <PopupHeader clickable={minimized} onClick={minimized ? onToggleMinimize : undefined}>
          <Box sx={{ position: "relative" }}>
            <Avatar src={avatarSrc} sx={{ width: 32, height: 32 }}>
              {name?.charAt(0)?.toUpperCase()}
            </Avatar>
            {/* Only show online status for one-to-one conversations */}
            {!selectedContact?.isGroup && (
              <OnlineStatus isOnline={oppositeUserOnline} />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: "13px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </Typography>
            {/* Show online/offline status text for one-to-one conversations */}
            {!selectedContact?.isGroup && !minimized && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "10px",
                  opacity: 0.9,
                  display: "block",
                  lineHeight: 1,
                }}
              >
                {oppositeUserOnline ? "online" : "offline"}
              </Typography>
            )}
          </Box>

          <PopupControls>
            {/* Group Info button - only show for group conversations when not minimized */}
            {!minimized && selectedContact?.isGroup && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenGroupInfo();
                }}
                sx={{ color: "#fff", p: 0.5 }}
                title="group info"
              >
                <InfoIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            
            {/* Menu button - only show when not minimized */}
            {!minimized && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuOpen(e);
                }}
                sx={{ color: "#fff", p: 0.5 }}
                title="menu"
              >
                <MoreVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            
            {/* Minimize button - only show when not minimized */}
            {!minimized && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleMinimize();
                }}
                sx={{ color: "#fff", p: 0.5 }}
                title="minimize"
              >
                <MinimizeIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            
            {/* Maximize/Restore button */}
            {!minimized && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleMaximize();
                }}
                sx={{ color: "#fff", p: 0.5 }}
                title={maximized ? "restore" : "maximize"}
              >
                {maximized ? (
                  <RestoreIcon sx={{ fontSize: 18 }} />
                ) : (
                  <MaximizeIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            )}
            
            {/* When minimized, show restore icon */}
            {minimized && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleMinimize();
                }}
                sx={{ color: "#fff", p: 0.5 }}
                title="restore"
              >
                <MaximizeIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              sx={{ color: "#fff", p: 0.5 }}
              title="close"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </PopupControls>
        </PopupHeader>
        
        {/* Chat Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 200,
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              border: "1px solid #E5E7EB",
            },
          }}
        >
          {/* helpers */}
          {(() => {
            const isMuted = conversationInfo?.isMuteUser?.some(
              (id) => String(id) === String(currentUserId)
            );
            const isBusiness = selectedContact?.isGroup && conversationInfo?.groupType === "business";
            const isOwnerOrTeam = conversationInfo?.users?.some(
              (u) => String(u?.userId?._id) === String(currentUserId) &&
                (u.userType === "owner" || u.userType === "teamMember")
            );
            console.log("[menu] currentUserId:", currentUserId, "| isOwnerOrTeam:", isOwnerOrTeam);
            console.log("[menu] users userType map:", conversationInfo?.users?.map(u => ({ id: u?.userId?._id, userType: u?.userType })));
            const isAdmin = conversationInfo?.admin?.some(
              (a) => String(a?.userId?._id) === String(currentUserId)
            );

            /* ONE-TO-ONE */
            if (!selectedContact?.isGroup) return <>
              <MenuItem disabled sx={{ opacity: 0.6 }}>
                <ListItemIcon><BlockIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>block</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleMenuAction("mute")}>
                <ListItemIcon><VolumeOffIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>{isMuted ? "unmute" : "mute"}</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleMenuAction("report")}>
                <ListItemIcon><ReportIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>report issue</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleMenuAction("clearChat")}>
                <ListItemIcon><DeleteSweepIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>clear chat</Typography>
              </MenuItem>
            </>;

            /* BUSINESS - OWNER/TEAM */
            if (isBusiness && isOwnerOrTeam) return <>
              <MenuItem onClick={() => handleMenuAction("addMember")}>
                <ListItemIcon><PersonAddIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>add member</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => handleMenuAction("viewOrder")}
                disabled={!canViewOrder}
                sx={!canViewOrder ? { opacity: 0.6 } : undefined}
              >
                <ListItemIcon><ScheduleIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>view order</Typography>
              </MenuItem>
              {canCloseOrder && (
                <MenuItem onClick={() => handleMenuAction("closeOrder")}>
                  <ListItemIcon><CloseIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                  <Typography variant="body2" sx={{ color: "#374151" }}>closed order</Typography>
                </MenuItem>
              )}
              <MenuItem onClick={() => handleMenuAction("mute")}>
                <ListItemIcon><VolumeOffIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>{isMuted ? "unmute" : "mute"}</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleMenuAction("report")}>
                <ListItemIcon><ReportIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>report issue</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleMenuAction("closeConversation")}>
                <ListItemIcon>
                  {conversationInfo?.isOpen === false
                    ? <LockOpenIcon sx={{ fontSize: 20, color: "#6B7280" }} />
                    : <CloseIcon sx={{ fontSize: 20, color: "#6B7280" }} />}
                </ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>
                  {conversationInfo?.isOpen === false ? "open conversation" : "close conversation"}
                </Typography>
              </MenuItem>
              <MenuItem disabled sx={{ opacity: 0.6 }}>
                <ListItemIcon><SettingsIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>budget setting</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleMenuAction("clearChat")}>
                <ListItemIcon><DeleteSweepIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>clear chat</Typography>
              </MenuItem>
            </>;

            /* BUSINESS - CUSTOMER */
            if (isBusiness && !isOwnerOrTeam) return <>
              <MenuItem onClick={() => handleMenuAction("addMember")}>
                <ListItemIcon><PersonAddIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>add member</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => handleMenuAction("viewOrder")}
                disabled={!canViewOrder}
                sx={!canViewOrder ? { opacity: 0.6 } : undefined}
              >
                <ListItemIcon><ScheduleIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>view order</Typography>
              </MenuItem>
              <MenuItem disabled sx={{ opacity: 0.6 }}>
                <ListItemIcon><StarIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>rate</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleMenuAction("report")}>
                <ListItemIcon><ReportIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>report issue</Typography>
              </MenuItem>
              <MenuItem disabled sx={{ opacity: 0.6 }}>
                <ListItemIcon><SettingsIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>budget setting</Typography>
              </MenuItem>
            </>;

            /* NON-BUSINESS GROUP */
            return <>
              <MenuItem onClick={() => handleMenuAction("addMember")}>
                <ListItemIcon><PersonAddIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>add member</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleMenuAction("mute")}>
                <ListItemIcon><VolumeOffIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>{isMuted ? "unmute" : "mute"}</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleMenuAction("report")}>
                <ListItemIcon><ReportIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>report issue</Typography>
              </MenuItem>
              {isAdmin && (
                <MenuItem onClick={() => handleMenuAction("deleteGroup")}>
                  <ListItemIcon><DeleteIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                  <Typography variant="body2" sx={{ color: "#374151" }}>delete group</Typography>
                </MenuItem>
              )}
              <MenuItem onClick={() => handleMenuAction("clearChat")}>
                <ListItemIcon><DeleteSweepIcon sx={{ fontSize: 20, color: "#6B7280" }} /></ListItemIcon>
                <Typography variant="body2" sx={{ color: "#374151" }}>clear chat</Typography>
              </MenuItem>
            </>;
          })()}

        </Menu>

      {/* Chat Content - Reuse existing ChatView */}
      {/* Keep ChatView mounted even when minimized to preserve messages */}
      <ChatContainer sx={{ display: minimized ? "none" : "flex" }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress size={24} sx={{ color: "#6200EE" }} />
          </Box>
        ) : selectedContact ? (
          <ChatView
            ref={chatViewRef}
            selectedContact={selectedContact}
            onClose={handleChatViewClose}
            isNewConversation={selectedContact.isNewConversation}
            isSidebarOpen={false}
            conversationInfo={conversationInfo}
            hideHeader={true}
            onViewOrderAvailabilityChange={setCanViewOrder}
            onCloseOrderAvailabilityChange={setCanCloseOrder}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              color: "#999",
              fontSize: "13px",
            }}
          >
            Unable to load chat
          </Box>
        )}
      </ChatContainer>
    </PopupContainer>
    </>
  );
};

export default ChatPopup;
